import { useEffect, useMemo, useState } from 'react';

import { App, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { FanguardApiContractsClientAssetModel } from 'src/api/generated';
import { useGlobal } from 'src/contexts/GlobalContext';
import { FileData } from 'src/pages/Devices/GroupDetails/TabPages/GroupDesign/components/FileUpload/FileUpload';

import {
  DrawerFooterButtons,
  DrawerTitle,
  StyledAssetDrawer,
  StyledAssetModal,
  StyledTabs,
} from './AssetPicker.styled';
import { AssetSelectGrid } from './AssetSelectGrid';
import { AssetSelectList } from './AssetSelectList';
import { AssetUploadPanel } from './AssetUploadPanel';
import { useAssetLibraryUpload } from './useAssetLibraryUpload';
import { assetToFileData, assetToPreviewFileData, filterAssetsByAccept } from './utils';

type AssetPickerTab = 'select' | 'upload';

// 'file' fields submit the bytes with the form, 'reference' fields only store the asset id.
export type AssetSelectionMode = 'file' | 'reference';

export interface AssetPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (fileData: FileData, asset?: FanguardApiContractsClientAssetModel) => void;
  assets?: FanguardApiContractsClientAssetModel[];
  isLoading?: boolean;
  title?: string;
  // Extensions only, e.g. '.jpg,.png'. The library is filtered by file name, so MIME tokens
  // would let through assets the field cannot take.
  accept?: string;
  maxSizeInMB?: number;
  minWidth?: number;
  minHeight?: number;
  defaultTab?: AssetPickerTab;
  selectedAssetId?: string | null;
  selectionMode?: AssetSelectionMode;
  uploadToLibrary?: boolean;
  onValidateFile?: (fileData: FileData) => boolean;
}

export const AssetPickerDialog = ({
  open,
  onClose,
  onSelect,
  assets = [],
  isLoading = false,
  title,
  accept,
  maxSizeInMB,
  minWidth,
  minHeight,
  defaultTab = 'select',
  selectedAssetId = null,
  selectionMode = 'file',
  uploadToLibrary = true,
  onValidateFile,
}: AssetPickerDialogProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useGlobal();
  const { message } = App.useApp();
  const { uploadFileToLibrary, isUploading } = useAssetLibraryUpload();

  const [activeTab, setActiveTab] = useState<AssetPickerTab>(defaultTab);
  const [pickedAssetId, setPickedAssetId] = useState<string | null>(selectedAssetId);
  const [pendingUpload, setPendingUpload] = useState<FileData | null>(null);
  const [isResolvingAsset, setIsResolvingAsset] = useState(false);

  const visibleAssets = useMemo(
    () => filterAssetsByAccept(assets, accept).filter((asset) => Boolean(asset.id)),
    [assets, accept],
  );

  useEffect(() => {
    if (!open) return;

    setActiveTab(isDesktop ? defaultTab : 'select');
    setPickedAssetId(selectedAssetId);
    setPendingUpload(null);
  }, [open, defaultTab, selectedAssetId, isDesktop]);

  const isUploadTab = activeTab === 'upload';
  const canConfirm = isUploadTab ? Boolean(pendingUpload) : Boolean(pickedAssetId);
  const isConfirming = isUploading || isResolvingAsset;

  const confirmUpload = async (fileData: FileData) => {
    const created = uploadToLibrary
      ? await uploadFileToLibrary(fileData, { resolveAsset: selectionMode === 'reference' })
      : undefined;

    // A reference field only stores an assetId, so without one there is nothing to select.
    if (selectionMode === 'reference' && !created?.id) {
      message.error(t('assets.uploadError'));

      return;
    }

    onSelect(fileData, created);
    onClose();
  };

  const confirmExistingAsset = async (assetId: string) => {
    const asset = visibleAssets.find((item) => item.id === assetId);
    if (!asset) return;

    if (selectionMode === 'reference') {
      onSelect(assetToPreviewFileData(asset), asset);
      onClose();

      return;
    }

    // Reference fields get the URL from the backend on read, but here the bytes are the payload,
    // so there is nothing to submit without one.
    if (!asset.url) {
      message.warning(t('assets.assetContentUnavailable'));

      return;
    }

    setIsResolvingAsset(true);
    try {
      const { fileData, isEmbedded } = await assetToFileData(asset);

      if (!isEmbedded) {
        message.warning(t('assets.assetContentUnavailable'));
      }

      onSelect(fileData, asset);
      onClose();
    } finally {
      setIsResolvingAsset(false);
    }
  };

  const handleConfirm = async () => {
    try {
      if (isUploadTab && pendingUpload) {
        await confirmUpload(pendingUpload);
        return;
      }

      if (pickedAssetId) {
        await confirmExistingAsset(pickedAssetId);
      }
    } catch {
      // The upload mutation surfaces its own error message, so the dialog just stays open.
    }
  };

  const selectPanel = isDesktop ? (
    <AssetSelectList
      assets={visibleAssets}
      selectedAssetId={pickedAssetId}
      onSelect={setPickedAssetId}
      isDesktop={isDesktop}
    />
  ) : (
    <AssetSelectGrid assets={visibleAssets} selectedAssetId={pickedAssetId} onSelect={setPickedAssetId} />
  );

  // The cancel button is disabled while confirming, but the mask and Esc bypass it and would let
  // the pending selection land in a field the user already walked away from.
  const handleDismiss = () => {
    if (isConfirming) return;

    onClose();
  };

  const cancelButton = (
    <Button onClick={onClose} disabled={isConfirming} data-testid="asset-picker-cancel">
      {t('general.cancel')}
    </Button>
  );

  const confirmButton = (
    <Button
      type="primary"
      onClick={handleConfirm}
      disabled={!canConfirm}
      loading={isConfirming}
      data-testid="asset-picker-select"
    >
      {t('assets.select')}
    </Button>
  );

  if (!isDesktop) {
    return (
      <StyledAssetDrawer
        open={open}
        onClose={handleDismiss}
        title={<DrawerTitle>{t('assets.selectAsset')}</DrawerTitle>}
        footer={
          <DrawerFooterButtons>
            {cancelButton}
            {confirmButton}
          </DrawerFooterButtons>
        }
        data-testid="asset-picker-drawer"
      >
        {selectPanel}
      </StyledAssetDrawer>
    );
  }

  return (
    <StyledAssetModal
      open={open}
      onCancel={handleDismiss}
      title={title ?? t('assets.title')}
      width={640}
      destroyOnHidden
      loading={isLoading}
      footer={
        <>
          {cancelButton}
          {confirmButton}
        </>
      }
      data-testid="asset-picker-modal"
    >
      <StyledTabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as AssetPickerTab)}
        items={[
          {
            key: 'select',
            label: t('assets.tabSelect'),
            children: selectPanel,
          },
          {
            key: 'upload',
            label: t('assets.tabUpload'),
            children: (
              <AssetUploadPanel
                value={pendingUpload}
                onChange={setPendingUpload}
                accept={accept}
                maxSizeInMB={maxSizeInMB}
                minWidth={minWidth}
                minHeight={minHeight}
                onValidateFile={onValidateFile}
              />
            ),
          },
        ]}
      />
    </StyledAssetModal>
  );
};
